import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LuStore } from "react-icons/lu";
import { FiSearch } from "react-icons/fi";

import ListPageShell from "../list-page/ListPageShell";
import ListPageHeader from "../list-page/ListPageHeader";
import ListPageSearch from "../list-page/ListPageSearch";
import ListPageEmpty from "../list-page/ListPageEmpty";
import ListPager from "../list-page/ListPager";

import BusinessTable from "./BusinessTable";
import BusinessDetails from "./BusinessDetails";
import { useGetBusinessesQuery } from "../../store/services/businessServices";

const PAGE_LIMIT = 10;

export default function BusinessesMain() {
  const navigate = useNavigate();

  const [filteredBusinesses, setFilteredBusinesses] = useState([]);
  const [pages, setPages] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [params, setParams] = useState({});
  const [searchTextInput, setSearchTextInput] = useState("");
  const [query] = useState(new URLSearchParams(window.location.search));
  const [selectedBusiness, setSelectedBusiness] = useState(null);

  const { data, isLoading, isFetching } = useGetBusinessesQuery(params);

  /* Hydrate from URL */
  useEffect(() => {
    const newParams = {};
    query.forEach((v, k) => (newParams[k] = v));
    setParams(newParams);
    if (query.has("page")) setCurrentPage(parseInt(query.get("page")));
    if (query.has("q")) setSearchTextInput(query.get("q"));
  }, [query]);

  /* Sync data */
  useEffect(() => {
    if (data) {
      setFilteredBusinesses(data?.data?.data || []);
      setPages({
        currentPage: data?.data?.currentPage,
        totalPages: data?.data?.totalPages,
        totalCount: data?.data?.totalCount,
      });
    }
  }, [data]);

  /* Reflect params into URL */
  useEffect(() => {
    const urlParams = new URLSearchParams(params).toString();
    navigate(`/businesses?${urlParams}`);
  }, [params, navigate]);

  /* Search debounce */
  const debounceRef = useRef(null);
  const handleSearchTextInputChange = (e) => {
    const text = e.target.value;
    setSearchTextInput(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (text.length > 2) {
      debounceRef.current = setTimeout(() => {
        setParams((p) => ({ ...p, q: text, page: 1 }));
        setCurrentPage(1);
      }, 400);
    } else {
      setParams((p) => {
        const { q, ...rest } = p;
        return rest;
      });
    }
  };

  const handleSort = (value) => {
    setParams((p) => {
      if (p.sort_by === value && p.sort_order === "asc") {
        const { sort_by, sort_order, ...rest } = p;
        return rest;
      }
      return {
        ...p,
        sort_by: value,
        sort_order: p.sort_by === value && p.sort_order === "desc" ? "asc" : "desc",
      };
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setPages((p) => ({ ...p, currentPage: page }));
    setParams((p) => ({ ...p, page }));
  };

  const hasFilters = !!(
    params.q ||
    params.is_active ||
    params.nui ||
    params.fiscal_number ||
    params.city ||
    params.municipality_no ||
    params.activity_code
  );

  const handleClearFilters = () => {
    setSearchTextInput("");
    setCurrentPage(1);
    setParams({});
  };

  const isEmpty = !isLoading && !isFetching && filteredBusinesses.length === 0;

  const totalCount = pages?.totalCount ?? 0;
  const rangeLabel = useMemo(() => {
    if (!totalCount) return "";
    const start = (currentPage - 1) * PAGE_LIMIT + 1;
    const end = Math.min(currentPage * PAGE_LIMIT, totalCount);
    return `Po shfaqen ${start}–${end} prej ${totalCount.toLocaleString()}`;
  }, [currentPage, totalCount]);

  return (
    <ListPageShell>
      <BusinessDetails
        business={selectedBusiness}
        isOpen={!!selectedBusiness}
        onClose={() => setSelectedBusiness(null)}
      />
      <ListPageHeader
        eyebrow={{ icon: <LuStore size={18} />, label: "Bizneset" }}
        title="Menaxho bizneset"
        subtitle={
          totalCount
            ? `${totalCount.toLocaleString()} biznese të regjistruar`
            : "Lista e bizneseve të regjistruar"
        }
      />

      <ListPageSearch
        value={searchTextInput}
        onChange={handleSearchTextInputChange}
        placeholder="Kërko sipas emrit, NUI-së ose aktivitetit..."
        hasFilters={hasFilters}
        onClearFilters={handleClearFilters}
      />

      {isEmpty ? (
        <ListPageEmpty
          icon={hasFilters ? FiSearch : LuStore}
          title={hasFilters ? "Asnjë biznes nuk u gjet" : "Ende asnjë biznes"}
          subtitle={
            hasFilters
              ? "Provoni të ndryshoni ose të pastroni filtrat për të parë më shumë rezultate."
              : "Bizneset do të shfaqen këtu automatikisht pasi të regjistrohen."
          }
          action={
            hasFilters && (
              <button
                type="button"
                onClick={handleClearFilters}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition"
              >
                Pastro filtrat
              </button>
            )
          }
        />
      ) : (
        <BusinessTable
          businesses={filteredBusinesses}
          isLoading={isLoading || isFetching}
          params={params}
          onSort={handleSort}
          onRowClick={setSelectedBusiness}
        />
      )}

      {!isEmpty && (
        <ListPager
          currentPage={currentPage}
          totalPages={pages?.totalPages || 1}
          onChange={handlePageChange}
          rangeLabel={rangeLabel}
        />
      )}
    </ListPageShell>
  );
}
